import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/App";
import { toast } from "sonner";
import { Check, Clock, Circle, Upload, Download, MessageSquare } from "lucide-react";

const estadoConfig = {
  pendiente: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500", dot: "bg-slate-300", label: "Pendiente" },
  en_curso: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", dot: "bg-amber-400", label: "En Curso" },
  completada: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", dot: "bg-emerald-500", label: "Completada" }
};

const nextEstado = { pendiente: "en_curso", en_curso: "completada", completada: "pendiente" };

export default function FasesProcesales({ casoId, fases, onUpdate }) {
  const [selectedFase, setSelectedFase] = useState(null);
  const [notas, setNotas] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingFaseId, setUploadingFaseId] = useState(null);
  const fileInputRef = useRef(null);

  const handleToggleEstado = async (fase) => {
    const nuevoEstado = nextEstado[fase.estado];
    try {
      await api.put(`/casos/${casoId}/fases/${fase.id}`, { estado: nuevoEstado });
      onUpdate();
    } catch { toast.error("Error actualizando fase"); }
  };

  const handleSaveNotas = async () => {
    if (!selectedFase) return;
    try {
      await api.put(`/casos/${casoId}/fases/${selectedFase.id}`, { notas });
      toast.success("Notas guardadas");
      onUpdate();
      setSelectedFase(null);
    } catch { toast.error("Error guardando notas"); }
  };

  const handleUploadDoc = (fase) => {
    setUploadingFaseId(fase.id);
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFaseId) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Solo PDF"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/casos/${casoId}/fases/${uploadingFaseId}/documento`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF subido");
      onUpdate();
    } catch { toast.error("Error subiendo PDF"); }
    finally { setUploading(false); setUploadingFaseId(null); e.target.value = ""; }
  };

  const handleDownloadDoc = async (fase) => {
    try {
      const res = await api.get(`/casos/${casoId}/fases/${fase.id}/documento`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.setAttribute("download", fase.documento_nombre || "documento.pdf");
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando"); }
  };

  const completadas = fases.filter(f => f.estado === "completada").length;
  const enCurso = fases.filter(f => f.estado === "en_curso").length;
  const progreso = fases.length > 0 ? Math.round((completadas / fases.length) * 100) : 0;

  return (
    <Card className="border-0 shadow-sm" data-testid="fases-procesales-card">
      <CardHeader className="pb-2 px-5 pt-5">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Fases Procesales</CardTitle>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-slate-400">{completadas}/{fases.length}</span>
              <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-500 rounded-full transition-all duration-700" style={{ width: `${progreso}%` }} data-testid="progress-bar" />
              </div>
              <span className="text-xs font-bold text-teal-600">{progreso}%</span>
            </div>
          </div>
          <div className="flex gap-1.5 text-xs">
            <span className="px-2 py-0.5 rounded-full badge-completada">{completadas}</span>
            <span className="px-2 py-0.5 rounded-full badge-en-curso">{enCurso}</span>
            <span className="px-2 py-0.5 rounded-full badge-pendiente">{fases.length - completadas - enCurso}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <input type="file" ref={fileInputRef} className="hidden" accept=".pdf" onChange={onFileSelected} />

        <div className="space-y-0" data-testid="fases-list">
          {fases.map((fase, idx) => {
            const cfg = estadoConfig[fase.estado] || estadoConfig.pendiente;
            const isLast = idx === fases.length - 1;

            return (
              <div key={fase.id} className={`phase-connector ${isLast ? 'last-child' : ''}`}>
                <div className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border transition-all hover:shadow-sm cursor-default ${cfg.bg} ${cfg.border}`} data-testid={`fase-${fase.id}`}>
                  {/* Status dot */}
                  <button
                    onClick={() => handleToggleEstado(fase)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all hover:scale-110 ${
                      fase.estado === "completada" ? "bg-emerald-500" : fase.estado === "en_curso" ? "bg-amber-400 animate-pulse" : "bg-slate-200 hover:bg-slate-300"
                    }`}
                    data-testid={`fase-toggle-${fase.id}`}
                    title={`Click → ${estadoConfig[nextEstado[fase.estado]].label}`}
                  >
                    {fase.estado === "completada" ? <Check className="h-3 w-3 text-white" /> :
                     fase.estado === "en_curso" ? <Clock className="h-3 w-3 text-white" /> :
                     <span className="w-2 h-2 rounded-full bg-slate-400" />}
                  </button>

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${fase.estado === "completada" ? "line-through opacity-50" : cfg.text}`}>
                      <span className="text-xs opacity-40 mr-1">{fase.orden}.</span>
                      {fase.nombre}
                    </span>
                    {fase.notas && <p className="text-[10px] text-slate-400 truncate mt-0.5">{fase.notas}</p>}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    {fase.documento_id ? (
                      <button onClick={() => handleDownloadDoc(fase)} className="p-1.5 rounded hover:bg-white/60 text-teal-600" title="Descargar PDF" data-testid={`fase-download-${fase.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button onClick={() => handleUploadDoc(fase)} className="p-1.5 rounded hover:bg-white/60 text-slate-400 hover:text-teal-600" disabled={uploading} title="Subir PDF" data-testid={`fase-upload-${fase.id}`}>
                        <Upload className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => { setSelectedFase(fase); setNotas(fase.notas || ""); }} className="p-1.5 rounded hover:bg-white/60 text-slate-400 hover:text-slate-600" title="Notas" data-testid={`fase-notas-${fase.id}`}>
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {!isLast && <div className="ml-[18px] w-px h-1 bg-slate-200" />}
              </div>
            );
          })}
        </div>

        <Dialog open={!!selectedFase} onOpenChange={(open) => !open && setSelectedFase(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-base">Notas — {selectedFase?.nombre}</DialogTitle>
            </DialogHeader>
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Agrega notas..." rows={3} className="text-sm" data-testid="fase-notas-textarea" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setSelectedFase(null)}>Cancelar</Button>
              <Button size="sm" onClick={handleSaveNotas} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-notas-btn">Guardar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
